import type { ReactNode, HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Tailwind drop-in replacement for MUI's `<Grid>` (container/item API).
 *
 * Renders a CSS grid: a `container` is a 12-column grid, and each `item`
 * spans `col-span-N` columns per breakpoint. This mirrors MUI's 12-column
 * model without any MUI/Emotion dependency.
 *
 * Breakpoint mapping (MUI is mobile-first, and so is Tailwind):
 *   - `xs`  → unprefixed (the base/mobile width)
 *   - `sm`/`md`/`lg`/`xl` → `sm:`/`md:`/`lg:`/`xl:` (min-width and up)
 *
 * Spacing mapping: MUI's spacing unit is 8px, Tailwind's is 4px, so a MUI
 * `spacing={n}` becomes `gap-{2n}`.
 *
 * NOTE: the class strings below are intentionally spelled out as literals so
 * Tailwind's JIT compiler can see them — never build them dynamically.
 */

type GridSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type GridSpacing = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

// MUI spacing (8px unit) → Tailwind gap (4px unit): gap = 2 × spacing.
const GAP: Record<GridSpacing, string> = {
  0: 'gap-0',
  1: 'gap-2',
  2: 'gap-4',
  3: 'gap-6',
  4: 'gap-8',
  5: 'gap-10',
  6: 'gap-12',
  7: 'gap-14',
  8: 'gap-16',
  9: 'gap-[4.5rem]',
  10: 'gap-20',
};

// xs = base (unprefixed) width.
const COL_SPAN_XS: Record<GridSize, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12',
};
const COL_SPAN_SM: Record<GridSize, string> = {
  1: 'sm:col-span-1',
  2: 'sm:col-span-2',
  3: 'sm:col-span-3',
  4: 'sm:col-span-4',
  5: 'sm:col-span-5',
  6: 'sm:col-span-6',
  7: 'sm:col-span-7',
  8: 'sm:col-span-8',
  9: 'sm:col-span-9',
  10: 'sm:col-span-10',
  11: 'sm:col-span-11',
  12: 'sm:col-span-12',
};
const COL_SPAN_MD: Record<GridSize, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
};
const COL_SPAN_LG: Record<GridSize, string> = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12',
};
const COL_SPAN_XL: Record<GridSize, string> = {
  1: 'xl:col-span-1',
  2: 'xl:col-span-2',
  3: 'xl:col-span-3',
  4: 'xl:col-span-4',
  5: 'xl:col-span-5',
  6: 'xl:col-span-6',
  7: 'xl:col-span-7',
  8: 'xl:col-span-8',
  9: 'xl:col-span-9',
  10: 'xl:col-span-10',
  11: 'xl:col-span-11',
  12: 'xl:col-span-12',
};

// NOTE: MUI's `justifyContent` is intentionally NOT supported. This container
// is a filled `grid grid-cols-12`, so there is no free inline space for
// `justify-content` to distribute — it would be a silent no-op. To center a
// narrower element, use a flex wrapper instead (see `BasicLayout`). `alignItems`
// IS supported: on a grid it aligns each item within its row (cross axis).
const ALIGN_ITEMS = {
  'flex-start': 'items-start',
  center: 'items-center',
  'flex-end': 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
} as const;

interface GridProps extends HTMLAttributes<HTMLDivElement> {
  container?: boolean;
  item?: boolean;
  xs?: GridSize;
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
  xl?: GridSize;
  spacing?: GridSpacing;
  alignItems?: keyof typeof ALIGN_ITEMS;
  zeroMinWidth?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * A single element can be both a container and an item (MUI's
 * `<Grid item container>`), so the classes for both roles are composed
 * together rather than branching to two components.
 */
export function Grid({
  container,
  // `item` is accepted for MUI API compatibility; the actual sizing is driven
  // by the xs/sm/md/lg/xl props. Destructured out so it never hits the DOM.
  item: _item,
  xs,
  sm,
  md,
  lg,
  xl,
  spacing = 0,
  alignItems,
  zeroMinWidth,
  className,
  children,
  ...rest
}: GridProps) {
  return (
    <div
      className={cn(
        // item role: column spans per breakpoint
        xs && COL_SPAN_XS[xs],
        sm && COL_SPAN_SM[sm],
        md && COL_SPAN_MD[md],
        lg && COL_SPAN_LG[lg],
        xl && COL_SPAN_XL[xl],
        zeroMinWidth && 'min-w-0',
        // container role: 12-column grid with gutter spacing
        container && 'grid grid-cols-12',
        container && GAP[spacing],
        container && alignItems && ALIGN_ITEMS[alignItems],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
